/**
 * Registros EXTRAS de ponto no dia.
 *
 * O colaborador pode liberar, na tela de registro, mais quadradinhos de batida para a data de
 * competência atual — de 2 em 2 (entrada + saída), no máximo 4 a mais e nunca além do tipo 8
 * (a jornada vai de `hora_<dia>1` a `hora_<dia>8`; os extras usam os tipos seguintes aos da
 * jornada, que a câmera e o servidor já aceitam). No dia seguinte (outra competência) a tela
 * volta ao normal.
 *
 * A liberação fica guardada NESTE aparelho (funciona offline). Batidas extras já feitas aparecem
 * sempre — mesmo em outro aparelho —, porque os quadradinhos também nascem das batidas do dia.
 */
import AsyncStorage from "@/platform/async-storage";

const CHAVE = "@tirvumais:pontos_extras";
/** Tipos de batida vão de 1 a 8. */
export const TIPO_MAXIMO = 8;
/** No máximo 4 quadradinhos a mais por dia… */
export const EXTRAS_MAXIMO = 4;
/** …liberados de 2 em 2 (entrada + saída). */
export const EXTRAS_POR_VEZ = 2;

type Mapa = Record<string, number>;

const ouvintes = new Set<() => void>();

function valorDe(ud: any, ...chaves: string[]): string {
  for (const fonte of [ud, ud?.done, ud?.colaborador]) {
    for (const k of chaves) {
      const v = fonte?.[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
  }
  return "";
}

/** Dono da liberação: empresa (hash) + colaborador — trocar de empresa não herda extras. */
export function donoDosExtras(userData: any): string {
  return `${valorDe(userData, "hash").toUpperCase()}|${valorDe(userData, "id_adm_colaborador", "idColaborador")}`;
}

const chaveDe = (dono: string, competencia: string) => `${dono}|${competencia}`;

async function lerMapa(): Promise<Mapa> {
  try {
    const raw = await AsyncStorage.getItem(CHAVE);
    const m = raw ? JSON.parse(raw) : {};
    return m && typeof m === "object" ? (m as Mapa) : {};
  } catch {
    return {};
  }
}

/** Quantos extras foram liberados para esta competência (0, 2 ou 4). */
export async function lerExtrasLiberados(dono: string, competencia: string): Promise<number> {
  if (!competencia) return 0;
  const m = await lerMapa();
  return Math.max(0, Math.min(EXTRAS_MAXIMO, Number(m[chaveDe(dono, competencia)]) || 0));
}

/** Libera mais 2 para a competência e devolve o novo total. Descarta as competências antigas. */
export async function liberarMaisExtras(dono: string, competencia: string): Promise<number> {
  const m = await lerMapa();
  const chave = chaveDe(dono, competencia);
  const novo = Math.min(EXTRAS_MAXIMO, (Number(m[chave]) || 0) + EXTRAS_POR_VEZ);
  // Só a competência atual importa ("no outro dia volta ao normal").
  const limpo: Mapa = {};
  for (const [k, v] of Object.entries(m)) if (k.endsWith(`|${competencia}`)) limpo[k] = v;
  limpo[chave] = novo;
  await AsyncStorage.setItem(CHAVE, JSON.stringify(limpo));
  ouvintes.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ouvinte com defeito não impede os outros */
    }
  });
  return novo;
}

/** Avisa quando a liberação muda (a Home e o Registro relêem). Devolve a função que cancela. */
export function observarExtras(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => {
    ouvintes.delete(fn);
  };
}

/** Quantos extras cabem depois da jornada (até 4, sem passar do tipo 8). */
export function espacoParaExtras(baseMaxTipo: number): number {
  return Math.max(0, Math.min(EXTRAS_MAXIMO, TIPO_MAXIMO - baseMaxTipo));
}

/**
 * Tipos dos quadradinhos extras a mostrar: os liberados neste aparelho, ou os necessários para
 * mostrar batidas extras que já existem (de outro aparelho, por exemplo), arredondados ao par.
 */
export function tiposExtras(
  baseMaxTipo: number,
  liberados: number,
  tiposRealizados: Iterable<number>,
): number[] {
  const espaco = espacoParaExtras(baseMaxTipo);
  let maiorRealizado = 0;
  for (const t of tiposRealizados) {
    if (t > baseMaxTipo && t <= TIPO_MAXIMO && t > maiorRealizado) maiorRealizado = t;
  }
  const exigidos = maiorRealizado > 0 ? Math.ceil((maiorRealizado - baseMaxTipo) / 2) * 2 : 0;
  const n = Math.min(espaco, Math.max(liberados, exigidos));
  return Array.from({ length: n }, (_, i) => baseMaxTipo + 1 + i);
}

/** Ainda dá para liberar mais 2? */
export function podeLiberarMaisExtras(baseMaxTipo: number, extrasMostrados: number): boolean {
  return extrasMostrados + EXTRAS_POR_VEZ <= espacoParaExtras(baseMaxTipo);
}

/** Dentro do bloco de extras, alterna começando por ENTRADA. */
export function extraEhEntrada(tipo: number, baseMaxTipo: number): boolean {
  return (tipo - baseMaxTipo) % 2 === 1;
}

/** "ENTRADA EXTRA", "SAÍDA EXTRA", e a partir do 2º par "ENTRADA EXTRA 2"… */
export function rotuloExtra(tipo: number, baseMaxTipo: number): string {
  const pos = tipo - baseMaxTipo;
  const par = Math.ceil(pos / 2);
  return `${extraEhEntrada(tipo, baseMaxTipo) ? "ENTRADA" : "SAÍDA"} EXTRA${par > 1 ? ` ${par}` : ""}`;
}
